import { Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth/auth.service';
import { PfBackgroundComponent } from './shared/ui/pf-background/pf-background.component';
import { PfNavComponent } from './shared/ui/pf-nav/pf-nav.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TranslateModule, PfBackgroundComponent, PfNavComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  readonly auth = inject(AuthService);

  readonly hidesHeader = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => this.isHeaderlessRoute(e.urlAfterRedirects)),
      startWith(this.isHeaderlessRoute(this.router.url)),
    ),
    { initialValue: this.isHeaderlessRoute(this.router.url) },
  );

  readonly hidesSideNav = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => this.isSideNavlessRoute(e.urlAfterRedirects)),
      startWith(this.isSideNavlessRoute(this.router.url)),
    ),
    { initialValue: this.isSideNavlessRoute(this.router.url) },
  );

  readonly langs = ['en', 'es'];
  private readonly fallbackLang = 'en';

  constructor() {
    const browserLang = this.translate.getBrowserLang() ?? this.fallbackLang;
    const resolved = this.langs.includes(browserLang) ? browserLang : this.fallbackLang;
    this.translate.use(resolved);
  }

  get currentLang(): string {
    return this.translate.getCurrentLang() ?? this.fallbackLang;
  }

  setLang(lang: string): void {
    this.translate.use(lang);
  }

  onLogout(): void {
    const navigateToLogin = () => void this.router.navigate(['/auth/login']);
    this.auth.logout().subscribe({ next: navigateToLogin, error: navigateToLogin });
  }

  private isHeaderlessRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return (
      path === '/' ||
      path === '' ||
      path.startsWith('/legal/') ||
      path.startsWith('/acceso-solicitado')
    );
  }

  private isSideNavlessRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return this.isHeaderlessRoute(path) || path.startsWith('/auth/');
  }
}
